import CycleMeasurement from '../../enums/cycle-measurement';
import Order from '../../enums/order.enum';
import { ChannelState } from '../../enums/channel-state.enum';
import CycleStatus from '../../enums/cycle-status.enum';
import { CycleController } from '../../store/models/controllers';
import getTempByMeasurement from '../../utils/getTempByMeasurement';
import { channelStateToEnum } from '../../utils/value-to-enum';
import { EXPIRATION_TIME_MS, SKIP_SYMBOL, STATE_COLORS } from './constants';
import { Cycle, SortCycleTableProps } from './types';

export const sortArray = <T>(array: T[], sortOption: SortCycleTableProps): T[] => {
  const { key, option } = sortOption;

  return array.slice().sort((a, b) => {
    const valueA = a[key];
    const valueB = b[key];

    if (valueA === SKIP_SYMBOL && valueB !== SKIP_SYMBOL) return 1;
    if (valueB === SKIP_SYMBOL && valueA !== SKIP_SYMBOL) return -1;

    if (valueA == null && valueB != null) return 1;
    if (valueB == null && valueA != null) return -1;
    if (valueA == null && valueB == null) return 0;

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return option === Order.ASC ? valueA - valueB : valueB - valueA;
    }

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return option === Order.ASC
        ? valueA.localeCompare(valueB, undefined, { numeric: true })
        : valueB.localeCompare(valueA, undefined, { numeric: true });
    }

    if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
      if (valueA === valueB) return 0;
      return option === Order.ASC ? (valueA ? 1 : -1) : (valueA ? -1 : 1);
    }

    return 0;
  });
};

const getDeltaT = (temp: number | string, sp: number | string, measurement: CycleMeasurement): string | number => {
  const tempNum = +temp;
  const spNum = +sp;

  if (isNaN(tempNum) || isNaN(spNum)) {
    return SKIP_SYMBOL;
  }

  const tempMeasured = getTempByMeasurement(measurement, tempNum);
  const spMeasured = getTempByMeasurement(measurement, spNum);

  return Math.round(tempMeasured - spMeasured);
};

export const generateIotDataByCycle = ({
  cycleId,
  iot,
  name,
  cycle_controller,
  device,
  measurement,
  status,
}: {
  cycleId: string;
  iot: any;
  name: string;
  cycle_controller: CycleController;
  device: string;
  measurement: CycleMeasurement;
  status: CycleStatus;
}): Cycle => {
  const deltaT = getDeltaT(iot.temp, iot.sp, measurement);

  return {
    cycleId,
    controller: cycle_controller.controller.name,
    name: name,
    degrees: iot.temp,
    sp: iot.sp,
    sk: iot.sk,
    present: iot.po,
    current: iot.oc,
    state: STATE_COLORS[iot.state],
    iotState: iot.manualFault ? ChannelState.FAULTED : channelStateToEnum(iot.state),
    status,
    controllerId: cycle_controller.controller.id,
    itemNumber: Number(device.split('-')[1]),
    isChannel: device.toLowerCase().startsWith('c-'),
    channelId: device,
    serial: cycle_controller.controller.serial_number,
    deltaT,
    itemDisconnected: isTimestampOutdated(iot.sk),
    manualFaultMessage: iot.manualFault?.message,
  };
};

export const isTimestampOutdated = (timestamp: number): boolean => {
  return Date.now() - timestamp >= EXPIRATION_TIME_MS;
};
